import React, { useState } from 'react';
import {
  Alert, Button, CircularProgress, Stack, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { Face } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { shortsAPI } from '../../services/api';
import ShortsRotationPanel from './ShortsRotationPanel';
import RotationCalendar from './RotationCalendar';
import ThumbnailFacePicker from './ThumbnailFacePicker';

/** A full episode's thumbnail/title rotation, with the same replace and
 *  face-pick actions as a Short (keyed by SxxEyy). */
const EpisodeRotation: React.FC<{ episodeKey: string; site: 'spoutlore' | 'mallbrats' }> = ({
  episodeKey, site,
}) => {
  const [picking, setPicking] = useState(false);
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const q = useQuery({
    queryKey: ['shorts', 'episode-rotation', episodeKey],
    queryFn: () => shortsAPI.getEpisodeRotation(episodeKey),
    refetchInterval: 60_000,
  });
  const clip = q.data?.clips?.[0];
  return (
    <>
      {q.isLoading ? <CircularProgress size={18} /> : q.error ? (
        <Alert severity="error">{(q.error as any)?.message}</Alert>
      ) : clip ? (
        <Stack spacing={1}>
          <ToggleButtonGroup size="small" exclusive value={view}
                             onChange={(_, v) => v && setView(v)} sx={{ alignSelf: 'flex-end' }}>
            <ToggleButton value="list">List</ToggleButton>
            <ToggleButton value="calendar">Calendar</ToggleButton>
          </ToggleButtonGroup>
          {view === 'list' ? (
            <ShortsRotationPanel clip={clip} site={site} landscape
                                 onChanged={() => q.refetch()}
                                 onPickFaces={() => setPicking(true)} />
          ) : (
            <RotationCalendar clips={[clip]} labels={{ [clip.clip_id]: episodeKey }}
                              onOpen={() => setView('list')} onChanged={() => q.refetch()} />
          )}
        </Stack>
      ) : (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1 }}>
          <Alert severity="info" sx={{ flex: 1, py: 0 }}>
            No thumbnail rotation for {episodeKey}: REAPER hasn't sent candidates for it.
          </Alert>
          <Button size="small" variant="outlined" startIcon={<Face />}
                  onClick={() => setPicking(true)}>
            Pick faces
          </Button>
        </Stack>
      )}
      <ThumbnailFacePicker targetKey={picking ? episodeKey : null} vod title={episodeKey}
                           onClose={() => setPicking(false)} onDone={() => q.refetch()} />
    </>
  );
};

export default EpisodeRotation;
